/**
 * T02 — 폐수배출시설 운영일지 (위탁처리)
 * ---------------------------------------------------------------------------
 * 「물환경보전법 시행규칙」 별지 제20호서식을 따른다. 칸 이름·순서·단위를
 * 서식 그대로 쓴다 — 입력 화면과 인쇄물의 말이 다르면 사용자는 어느 칸이
 * 서식의 어디로 가는지 매번 짐작해야 한다.
 *
 *   가동시간 ( ~ ) · 원료사용량 · 용수사용량(L) · 폐수발생량(L) ·
 *   폐수위탁처리 [위탁량(L) · 위탁·수탁 확인서 일련번호 · 처리업소명] ·
 *   보관폐수량(L) · 기타 중요사항
 *
 * 단위는 L 다
 *   서식이 리터로 정해 두었다. 예전 화면은 ㎥ 로 적혀 있었는데, 같은 숫자가
 *   천 배 차이 나는 뜻이 된다. 서식을 따른다.
 *
 * 없앤 것
 *   대상 시설    이 서식은 사업장 단위 장부다. 시설을 고를 칸이 없다.
 *   시설 보수    서식에 없다. 편집기가 이 서식에서는 보수 구역을 그리지 않는다.
 *   사업자번호   처리업체의 사업자등록번호는 서식에 적을 자리가 없다.
 */

import { Button, Card, Descriptions, Table, Typography } from 'antd';
import type { MaterialRow, WaterConsignBody } from '@eco/shared';
import { operatingHoursBetween } from '@eco/shared';
import { NumberCell, TextCell, TimeCell, appendRow, patchRow, removeRow } from './rows';

const { Text } = Typography;

const EMPTY_MATERIAL: MaterialRow = {
  materialName: '',
  quantity: null,
  unit: null,
  note: null,
  sortOrder: 0,
};

/** 인쇄 서식의 원료사용량 기본 칸 수. 원료가 더 많으면 칸이 늘어난다. */
export const CONSIGN_MATERIAL_COLUMNS = 4;

export function WaterConsignForm({
  body,
  materials,
  locked,
  onBody,
  onMaterials,
}: {
  body: WaterConsignBody;
  materials: MaterialRow[];
  locked: boolean;
  onBody: (body: WaterConsignBody) => void;
  onMaterials: (rows: MaterialRow[]) => void;
}) {
  const set = <K extends keyof WaterConsignBody>(key: K, value: WaterConsignBody[K]) =>
    onBody({ ...body, [key]: value });

  /** 시간대를 고치면 가동시간을 같이 맞춘다. 대기 운영기록부와 같은 규칙이다. */
  const setUptime = (part: Pick<Partial<WaterConsignBody>, 'uptimeStart' | 'uptimeEnd'>) => {
    const merged = { ...body, ...part };
    onBody({
      ...merged,
      operatingHours: operatingHoursBetween(merged.uptimeStart, merged.uptimeEnd),
    });
  };

  return (
    <>
      <Card title="폐수배출시설 운영일지" size="small">
        <Descriptions bordered size="small" column={{ xs: 1, sm: 2, xl: 3 }}>
          <Descriptions.Item label="가동시간 (시작)">
            <TimeCell
              value={body.uptimeStart}
              disabled={locked}
              onChange={(v) => setUptime({ uptimeStart: v })}
            />
          </Descriptions.Item>
          <Descriptions.Item label="가동시간 (종료)">
            <TimeCell
              value={body.uptimeEnd}
              disabled={locked}
              onChange={(v) => setUptime({ uptimeEnd: v })}
            />
          </Descriptions.Item>
          <Descriptions.Item label="가동시간 계(h)">
            <NumberCell
              value={body.operatingHours}
              disabled={locked}
              min={0}
              max={24}
              step={0.5}
              placeholder="자동"
              onChange={(v) => set('operatingHours', v)}
            />
          </Descriptions.Item>

          <Descriptions.Item label="용수사용량(L)">
            <NumberCell
              value={body.waterVolume}
              disabled={locked}
              min={0}
              onChange={(v) => set('waterVolume', v)}
            />
          </Descriptions.Item>
          <Descriptions.Item label="폐수발생량(L)" span={2}>
            <NumberCell
              value={body.wastewaterGeneration}
              disabled={locked}
              min={0}
              onChange={(v) => set('wastewaterGeneration', v)}
            />
          </Descriptions.Item>
        </Descriptions>
      </Card>

      <Card title="폐수위탁처리" size="small">
        <Descriptions bordered size="small" column={{ xs: 1, sm: 2, xl: 3 }}>
          <Descriptions.Item label="위탁량(L)">
            <NumberCell
              value={body.consignmentAmount}
              disabled={locked}
              min={0}
              onChange={(v) => set('consignmentAmount', v)}
            />
          </Descriptions.Item>
          <Descriptions.Item label="위탁·수탁 확인서 일련번호">
            <TextCell
              value={body.confirmationSerialNo}
              disabled={locked}
              maxLength={60}
              onChange={(v) => set('confirmationSerialNo', v)}
            />
          </Descriptions.Item>
          <Descriptions.Item label="처리업소명">
            <TextCell
              value={body.processingCompany}
              disabled={locked}
              maxLength={200}
              onChange={(v) => set('processingCompany', v)}
            />
          </Descriptions.Item>

          <Descriptions.Item label="보관폐수량(L)">
            <NumberCell
              value={body.storageWastewater}
              disabled={locked}
              min={0}
              onChange={(v) => set('storageWastewater', v)}
            />
          </Descriptions.Item>
          <Descriptions.Item label="기타 중요사항" span={2}>
            <TextCell
              value={body.note}
              disabled={locked}
              maxLength={1000}
              onChange={(v) => set('note', v)}
            />
          </Descriptions.Item>
        </Descriptions>
      </Card>

      <Card
        title="원료사용량"
        size="small"
        extra={
          !locked && (
            <Button size="small" onClick={() => onMaterials(appendRow(materials, EMPTY_MATERIAL))}>
              행 추가
            </Button>
          )
        }
      >
        <Table<MaterialRow>
          className="eco-grid"
          rowKey={(_, i) => String(i)}
          size="small"
          pagination={false}
          dataSource={materials}
          locale={{ emptyText: '사용한 원료가 없으면 비워 둡니다.' }}
          columns={[
            {
              title: '원료명',
              render: (_, row, i) => (
                <TextCell
                  value={row.materialName}
                  disabled={locked}
                  onChange={(v) => onMaterials(patchRow(materials, i, { materialName: v ?? '' }))}
                />
              ),
            },
            {
              title: '사용량',
              width: 150,
              render: (_, row, i) => (
                <NumberCell
                  value={row.quantity}
                  disabled={locked}
                  min={0}
                  onChange={(v) => onMaterials(patchRow(materials, i, { quantity: v }))}
                />
              ),
            },
            {
              title: '단위',
              width: 100,
              render: (_, row, i) => (
                <TextCell
                  value={row.unit}
                  disabled={locked}
                  maxLength={20}
                  onChange={(v) => onMaterials(patchRow(materials, i, { unit: v }))}
                />
              ),
            },
            {
              title: '',
              width: 60,
              render: (_, __, i) =>
                !locked && (
                  <Button
                    size="small"
                    type="link"
                    danger
                    onClick={() => onMaterials(removeRow(materials, i))}
                  >
                    삭제
                  </Button>
                ),
            },
          ]}
        />
        {/* 서식의 원료사용량은 네 칸이다. 넘으면 어떻게 되는지 미리 알린다. */}
        <Text type="secondary" style={{ fontSize: 12 }}>
          인쇄 서식의 원료사용량은 기본 {CONSIGN_MATERIAL_COLUMNS}칸이며, 원료가 더 많으면 그만큼
          칸이 늘어납니다.
        </Text>
      </Card>
    </>
  );
}
